import React, { use, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus, Trash2, Ship, ChevronDown, ChevronUp, Package,
    MapPin, DollarSign, Anchor, Calendar, AlertTriangle
} from 'lucide-react';
import { useDispatch, useSelector } from "react-redux";

import { SearchBar } from '@/components/SearchBox/SearchBar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import DatePicker from "react-datepicker";
import InputField from '../ui/InputField';
import { Textarea } from '../ui/textarea';
import { showToast } from '@/redux/Toast/actions';
import { set } from 'date-fns';
import moment from 'moment';
import { callLoader } from '@/redux/Loader/actions';
import { GetVesselSchedule } from '@/redux/Booking/actions';
import { useAlertModal } from "@/hooks/useAlertModal";
import AlertModal from '@/components/Modal/AlertModal';

const MultiCarrierSection = ({ CarrierDetails, carrierIndex, onChange, onDelete, allCarrierList = [], originPortCode, destinationPortCode, searchDate }) => {

    const dispatch = useDispatch();
    const alert = useAlertModal();
    const [expandedCarriers, setExpandedCarriers] = useState({});
    const [formData, setFormData] = useState(CarrierDetails);
    const [carriers, setCarriers] = useState([]);
    const [vessel, setVessel] = useState([]);
    const [container, setContainer] = useState([]);

    // Vessel Schedule state
    const [showModal, setShowModal] = useState(false);
    const [vesselList, setVesselList] = useState([]);
    const [selectedVessels, setSelectedVessels] = useState([]);
    const [error, setError] = useState("");


    const { countryList, documentTypePurchaseList, documentTypeSalesList, incoTermsList, quantityTypeList, salesPersonList, uomTypeList, commodityGroupList, containerTypeList, operatorList, moveTypeList, fileManagerList, currency_list, charges_basis_list } = useSelector((state) => state.common);
    const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
    const { isAnimate } = useSelector((state) => state.toast);
    const unitToCm = {
        CM: 1,
        INCH: 2.54,
        FEET: 30.48,
        MT: 100,
    };

    const inputClass = "w-full px-3 py-[0.6rem] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all";
    const selectClass = "w-full px-3 py-[0.5rem] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none cursor-pointer";


    useEffect(() => {
        setCarriers(CarrierDetails?.rateCharges || []);
        _setContainer(CarrierDetails?.rateQuoteContainer || []);
        setVessel(CarrierDetails?.ratequoteVessel || []);
        setFormData(CarrierDetails);
    }, [CarrierDetails]);

    useEffect(() => {
        var data = formData;
        data.rateCharges = carriers;
        data.rateQuoteContainer = container;
        data.ratequoteVessel = vessel;
        setFormData(data);
        onChange && onChange(data, carrierIndex);
    }, [formData, container, vessel, carriers]);

    const _setContainer = (allContainers) => {
        const activeAllContainers = allContainers.filter(c => c?.isDeleted !== 1) || [];
        const deletedContainers = allContainers.filter(c => c?.isDeleted === 1) || [];
        setContainer([...deletedContainers, ...activeAllContainers]);
    }

    // Toggle carrier expansion
    const toggleCarrier = (index) => {
        setExpandedCarriers(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        console.log({ name, value, type, checked });
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    //  Rate Charges Handlers
    const addChargeRow = () => {
        const activeContainersCount = container.filter(c => c?.isDeleted !== 1).length;
        if (activeContainersCount === 0) {
            dispatch(showToast("Please add at least one active container!", "error"));
            return;
        }
        setCarriers(prev => [...prev,
        {
            "rateid": 0,
            "carriersno": formData?.carrierSno || 0,
            "freightid": 0,
            "chargedescription": "",
            "all_in": false,
            "currency": "USD",
            "roe": 1,
            "set1sellrate": 0,
            "set1buyrate": 0,
            "set2sellrate": 0,
            "set2buyrate": 0,
            "set3sellrate": 0,
            "set3buyrate": 0,
            "desccode": "",
            "basis": "",
            "isdeleted": false,
            "iscustadditionalrate": 0,
            "vendorid": 0,
            "vendor_name": "",
            "paymentmode": ""
        }
        ]);
    };
    const handleRateChange = (index, field, value) => {
        const updated = [...carriers];
        updated[index][field] = value;
        calculateTotals(updated);
        setCarriers(updated);
    };
    const _getContainerName = (index) => {
        const notDeletedCount = container.filter(r => r?.isDeleted == 0);
        return notDeletedCount?.[index]?.contTypeName;
    }
    const calculateTotals = (data = []) => {
        const totals = data.reduce(
            (acc, item) => {
                acc.set1TotalSell += parseFloat(item.set1sellrate) || 0;
                acc.set1TotalBuy += parseFloat(item.set1buyrate) || 0;
                acc.set2TotalSell += parseFloat(item.set2sellrate) || 0;
                acc.set2TotalBuy += parseFloat(item.set2buyrate) || 0;
                acc.set3TotalSell += parseFloat(item.set3sellrate) || 0;
                acc.set3TotalBuy += parseFloat(item.set3buyrate) || 0;
                return acc;
            },
            { set1TotalSell: 0, set1TotalBuy: 0, set2TotalSell: 0, set2TotalBuy: 0, set3TotalSell: 0, set3TotalBuy: 0 }
        );

        handleInputChange({ target: { name: 'set1TotalSell', value: totals.set1TotalSell } });
        handleInputChange({ target: { name: 'set1TotalBuy', value: totals.set1TotalBuy } });
        handleInputChange({ target: { name: 'set2TotalSell', value: totals.set2TotalSell } });
        handleInputChange({ target: { name: 'set2TotalBuy', value: totals.set2TotalBuy } });
        handleInputChange({ target: { name: 'set3TotalSell', value: totals.set3TotalSell } });
        handleInputChange({ target: { name: 'set3TotalBuy', value: totals.set3TotalBuy } });

        handleInputChange({ target: { name: 'set1Profit', value: (totals.set1TotalSell - totals.set1TotalBuy) } });
        handleInputChange({ target: { name: 'set2Profit', value: (totals.set2TotalSell - totals.set2TotalBuy) } });
        handleInputChange({ target: { name: 'set3Profit', value: (totals.set3TotalSell - totals.set3TotalBuy) } });

        handleInputChange({
            target: {
                name: 'set1ProfitMargin',
                value: (totals.set1TotalSell > 0 && totals.set1TotalBuy >= 0 ? ((totals.set1TotalSell - totals.set1TotalBuy) / totals.set1TotalBuy) * 100 : 0)
            }
        });
        handleInputChange({
            target: {
                name: 'set2ProfitMargin',
                value: (totals.set2TotalSell > 0 && totals.set2TotalBuy >= 0 ? ((totals.set2TotalSell - totals.set2TotalBuy) / totals.set2TotalBuy) * 100 : 0)
            }
        });
        handleInputChange({
            target: {
                name: 'set3ProfitMargin',
                value: (totals.set3TotalSell > 0 && totals.set3TotalBuy >= 0 ? ((totals.set3TotalSell - totals.set3TotalBuy) / totals.set3TotalBuy) * 100 : 0)
            }
        });

    }

    // Container Handlers
    const handleContainerChange = (index, field, value) => {
        const updated = [...container];
        updated[index][field] = value;
        updated[index]['lineSeqno'] = index + 1;
        setContainer(updated);
    };
    const addContainerChange = () => {
        const notDeletedCount = container.filter(r => r?.isDeleted == 0).length;
        if (notDeletedCount >= 3) {
            // toast.error('Max 3 container can add!');
            dispatch(showToast("Max 3 container can add!", "error"));
            return;
        }
        setContainer(prev => [
            ...prev,
            {
                "containerId": 0,
                "numberOfContainer": 1,
                "contTypeCode": "42G0",
                "contTypeName": "40 STD",
                "lineSeqno": 0,
                "carrierId": formData?.carrierSno || 0,
                "freightId": 0,
                "booked": false,
                "isDeleted": 0
            }
        ]);
    };
    const deleteContainerChange = (index, field, value, tempIndex) => {
        alert.showAlert({
            title: "Confirm Deletion",
            description: "Are you sure you want to delete this container? If you delete, the charges will be removed automatically related to this container.",
            icon: AlertTriangle,
            iconColor: "text-yellow-500",
            confirmText: "Yes, Delete",
            onConfirm: () => {
                const updated = [...container];
                updated[index][field] = value;
                updated[index]['lineSeqno'] = index + 1;
                const isDeleted = updated?.filter(r => r?.isDeleted == 1) || [];
                const isActived = updated?.filter(r => r?.isDeleted == 0) || [];
                setContainer([...isDeleted, ...isActived]);
                _clearContainerRelatedCharges(tempIndex);
            },
            cancelText: "Cancel",
        });
    }
    const _clearContainerRelatedCharges = (index) => {
        if (index === 0) {
            const allCarriers = [...carriers];
            allCarriers.forEach(carrier => {
                carrier.isdeleted = true;
            });
            setCarriers(allCarriers);
            calculateTotals();
        } else {
            const allCarriers = [...carriers];
            allCarriers.forEach(carrier => {
                carrier["set" + (index + 1) + "sellrate"] = 0.00;
                carrier["set" + (index + 1) + "buyrate"] = 0.00;
            });
            setCarriers(allCarriers);
            calculateTotals(allCarriers);
        }
    };

    // Vessel Handlers
    const handleVesselChange = (index, field, value) => {
        console.log(index, field, value);
        const updated = [...vessel];
        updated[index][field] = value;
        updated[index]['lineSeqno'] = index + 1;
        setVessel(updated);
    };
    const addVesselCharge = () => {
        const notDeletedCount = vessel.filter(r => r?.isDeleted == 0).length;
        if (notDeletedCount >= 3) {
            // toast.error('Max 3 Vessel Charge Can Add!');
            dispatch(showToast("Max 3 Vessel Charge Can Add!", "error"));
            return;
        }
        setVessel(prev => [
            ...prev,
            {
                "id": 0,
                "vesselId": 0,
                "vesselName": "",
                "voyage": "",
                "etd": null,
                "lineSeqNo": 0,
                "eta": null,
                "transitTime": 0,
                "carrierId": formData?.carrierSno || 0,
                "freightId": 0,
                "cargoCutoff": null,
                "isDeleted": 0
            }
        ]);
    };
    const countDays = (startDate, endDate) => {
        const start = moment(startDate, "DD MMM YYYY", true);
        const end = moment(endDate, "DD MMM YYYY", true);
        if (!start.isValid() || !end.isValid()) {
            return "";
        }
        const days = end.diff(start, "days");
        return isNaN(days) ? "" : days.toString();
    }


    //
    const _checkCarrierDuplicate = (carrierId) => {
        const duplicate = allCarrierList?.some((c, i) => c.carrierSno === carrierId && i !== carrierIndex);
        return duplicate;
    }


    const handleVesselOverrideFromModal = (data = []) => {
        if (!Array.isArray(data)) return;

        const mapped = data.map((v, index) => ({

            vesselName: v.vesselName || "",
            voyage: v.voyageNumber || "",
            etd: v.originDepartureDate || null,
            eta: v.destinationArrivalDate || null,
            cargoCutoff: v.terminalCutoff || null,
            transitTime: v.totalDuration || "",

        }));

        setVessel(mapped);
    };

    const AddListofVessel = async () => {
        debugger;
        const formatDate = (date) => date ? new Date(date).toISOString().split("T")[0] : "";
        try {
            dispatch(callLoader(1)); // 🔥 Loader ON
            const payloadVessel = {
                CarrierScac: formData?.carrierSCAC,
                originPortCode: originPortCode,
                destinationPortCode: destinationPortCode,
                searchDate: searchDate
            };

            const list = await dispatch(GetVesselSchedule(payloadVessel));

            console.log("Vessel List:", list);  // check array

            setVesselList(list);
            setShowModal(true);

        } catch (error) {
            console.error("Error fetching vessel schedule:", error);
            setVesselList([]); // safety
        } finally {
            dispatch(callLoader(0)); // 🔥 Loader OFF (always run)
        }
    };

    const handleCheckboxChange = (item, isChecked) => {
        let updated = [...selectedVessels];

        const notDeletedCount = vessel.filter(r => r?.isDeleted == 0).length;
        if (notDeletedCount >= 3) {
            // toast.error('Max 3 Vessel Charge Can Add!');
            dispatch(showToast("Max 3 Vessel Charge Can Add!", "error"));
            return;
        }

        if (isChecked) {
            // 🔥 combined validation
            if (updated.length + notDeletedCount >= 3) {
                dispatch(showToast("Max 3 Vessel Charge Can Add!", "error"));
                return;
            }

            if (updated.length >= 3) {
                setError("You can select maximum 3 vessels only");
                return;
            }
            updated.push(item);
        } else {
            updated = updated.filter(v => v !== item);
        }

        setError("");
        setSelectedVessels(updated);
    };

    const handleSaveVessels = () => {
        debugger;
        const safeSelected = Array.isArray(selectedVessels) ? selectedVessels : [];
        if (safeSelected.length === 0) {
            setError("Please select at least one vessel");
            return;
        }
        const toISO = (date) => date ? new Date(date).toISOString() : null;
        const formatted = safeSelected.map((v, index) => ({
            vesselName: v.vesselName || "",
            voyage: v.voyageNumber || "",
            cargoCutoff: toISO(v.terminalCutoff),
            eta: toISO(v.destinationArrivalDate),
            etd: toISO(v.originDepartureDate),
            transitTime: v.totalDuration ? String(v.totalDuration) : null,
            freightId: 0,
            id: 0,
            isDeleted: 0,
            lineSeqNo: index + 1,
            carrierId: formData?.carrierSno || 0,
            vesselId: v.vesselId || 0
        }));
        setVessel(prev => [...prev, ...formatted]);
        setSelectedVessels([]);
        setShowModal(false);
        setError("");
    };

    const settAllin = (prew) => {
        const updatedCarriers = carriers.map(item => ({
            ...item,
            all_in: prew
        }));
        setCarriers(updatedCarriers);
    }

    return (
        <div className="space-y-4" >
            {/* Add Carrier Button */}

            {/* Carrier Cards */}
            <div AnimatePresence >
                <motion.div
                    key={formData?.carrierSno || carrierIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden"
                >
                    {/* Carrier Header */}
                    <div
                        className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 cursor-pointer"
                        onClick={() => toggleCarrier(carrierIndex)}
                    >
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/20 rounded-lg">
                                <Ship className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h3 className="text-white font-semibold">
                                    Carrier {carrierIndex + 1}: {formData?.carrierName || 'Select Carrier'}
                                </h3>
                                <p className="text-cyan-100 text-xs">
                                    {formData?.rateQuoteContainer?.filter(c => c.isDeleted !== 1).length || 0} containers,
                                    {formData?.ratequoteVessel?.filter(v => !v.isDeleted).length || 0} vessels,
                                    {formData?.rateCharges?.filter(c => !c.isdeleted).length || 0} charges
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={(e) => {
                                    onDelete();
                                }}
                                className="p-2 bg-red-500/20 hover:bg-red-500/40 rounded-lg transition-colors"
                                title="Remove Carrier"
                            >
                                <Trash2 className="w-4 h-4 text-white" />
                            </button>
                            {expandedCarriers[carrierIndex] ? (
                                <ChevronUp className="w-5 h-5 text-white" />
                            ) : (
                                <ChevronDown className="w-5 h-5 text-white" />
                            )}
                        </div>
                    </div>

                    {/* Carrier Content */}
                    <AnimatePresence>
                        {expandedCarriers[carrierIndex] && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="p-4 space-y-6"
                            >
                                {/* Carrier Info */}
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                                            Carrier <span className="text-red-500">*</span>
                                        </label>
                                        <SearchBar
                                            searchAPI={'v1/AutoSuggestion/GetCarrierSuggestion?Prefix='}
                                            selectReacord={(d) => {
                                                if (_checkCarrierDuplicate(d?.[0])) {
                                                    dispatch(showToast('Carrier already added.', 'error'));
                                                    const oldname = formData?.carrierName || '';
                                                    handleInputChange({ target: { name: 'carrierName', value: ' ' } });
                                                    setTimeout(() => {
                                                        handleInputChange({ target: { name: 'carrierName', value: oldname } });
                                                    }, 500);
                                                    return;
                                                } else {
                                                    handleInputChange({ target: { name: 'carrierName', value: d?.[1] } });
                                                    handleInputChange({ target: { name: 'carrierSno', value: d?.[0] } });
                                                    handleInputChange({ target: { name: 'carrierSCAC', value: d?.[2] } });
                                                    handleInputChange({ target: { name: 'carrierContract', value: d?.[3] } });
                                                    handleInputChange({ target: { name: 'carrierNotes', value: d?.[4] } });
                                                }
                                            }}
                                            value={formData?.carrierName}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                                            Carrier Contact / R/Q #
                                        </label>
                                        <input
                                            type="text"
                                            value={formData?.carrierContract || ''}
                                            onChange={(e) => {
                                                handleInputChange({ target: { name: 'carrierContract', value: e.target.value } });
                                            }}
                                            className={inputClass}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                                            Carrier Notes
                                        </label>
                                        <input
                                            type="text"
                                            value={formData?.carrierNotes || ''}
                                            onChange={(e) => {
                                                handleInputChange({ target: { name: 'carrierNotes', value: e.target.value } });
                                            }}
                                            className={inputClass}
                                        />
                                    </div>
                                </div>

                                {/* Container & Commodity Section */}
                                <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                                    <div className="grid grid-cols-2 items-center mb-3">
                                        <div className="flex items-center gap-2">
                                            <Package className="w-4 h-4 text-orange-500" />
                                            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Container Details</h4>
                                        </div>
                                        <Button variant={'outline'} onClick={addContainerChange} className="justify-self-end">
                                            <Plus /> Add Container
                                        </Button>
                                    </div>
                                    <div className="grid grid-cols-1 gap-3">
                                        <div>
                                            <div className='mt-4'>
                                                {
                                                    container?.map((cnt, index) => {
                                                        const deactiveContainerCount = container?.filter(c => c?.isDeleted == 1)?.length || 0;
                                                        return cnt?.isDeleted !== 1 && (
                                                            <div key={index} className="grid grid-cols-11 gap-2 mb-2 items-center">
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    value={cnt?.numberOfContainer}
                                                                    onChange={(e) => {
                                                                        handleContainerChange(index, 'numberOfContainer', e.target.value)
                                                                    }}
                                                                    className={`w-20 ${inputClass} col-span-5`}
                                                                />
                                                                <select
                                                                    value={cnt?.contTypeCode}
                                                                    onChange={(e) => {
                                                                        handleContainerChange(index, 'contTypeCode', e.target.value)
                                                                        handleContainerChange(index, 'contTypeName', e.target.options[e.target.selectedIndex].text)
                                                                    }}
                                                                    className={`flex-1 ${selectClass} col-span-5`}
                                                                >
                                                                    <option value="">Select</option>
                                                                    {
                                                                        containerTypeList?.map((container, key) => (
                                                                            <option key={key} value={container.moveTypeId}>{container.moveType}</option>
                                                                        ))
                                                                    }
                                                                </select>
                                                                <div className=''>
                                                                    {
                                                                        container?.length - 1 == index && (
                                                                            <Button
                                                                                variant={'outline'}
                                                                                onClick={() => deleteContainerChange(index, 'isDeleted', 1, index - deactiveContainerCount)}
                                                                            >
                                                                                <Trash2 />
                                                                            </Button>
                                                                        )
                                                                    }
                                                                </div>
                                                            </div>
                                                        )
                                                    })
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Vessel Section */}
                                <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <Anchor className="w-4 h-4 text-blue-500" />
                                            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Vessel Information</h4>
                                        </div>
                                        <button type="button" onClick={AddListofVessel} className="px-3 py-1 bg-green-600 text-white rounded">GetVessel</button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={addVesselCharge}
                                            className="text-xs"
                                        >
                                            <Plus className="w-3 h-3 mr-1" /> Add Vessel
                                        </Button>
                                    </div>
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        className="p-3"
                                    >
                                        <div key={1}>
                                            <div className=" overflow-x-auto overflow-y-visible pb-[20vh]">
                                                <table className="w-full text-xs ev_ar_table">
                                                    <thead>
                                                        <tr className="bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-800">
                                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Vessel</th>
                                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Voyage</th>
                                                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">C/O</th>
                                                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">ETD</th>
                                                            <th className="px-4 py-3 text-center text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase bg-emerald-50 dark:bg-emerald-900/20">ETA</th>
                                                            <th className="px-4 py-3 text-center text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase bg-amber-50 dark:bg-amber-900/20">Transit Time</th>
                                                            <th className="px-4 py-3 text-center text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase bg-amber-50 dark:bg-amber-900/20"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                                        {
                                                            vessel?.map((v, i) => v?.isDeleted !== 1 && (
                                                                <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                                                        <SearchBar
                                                                            searchAPI={'v1/AutoSuggestion/GetVesselList?Prefix='}
                                                                            value={v?.vesselName || ""}

                                                                            onchange={(value) => {
                                                                                setVessel(prev => {
                                                                                    const updated = [...prev];

                                                                                    updated[i] = {
                                                                                        ...updated[i],
                                                                                        vesselName: value,
                                                                                        vesselId: 0 // reset when typing manually
                                                                                    };

                                                                                    return updated;
                                                                                });
                                                                            }}

                                                                            selectReacord={(d) => {
                                                                                setVessel(prev => {
                                                                                    const updated = [...prev];

                                                                                    updated[i] = {
                                                                                        ...updated[i],
                                                                                        vesselName: d?.[1] || "",
                                                                                        vesselId: d?.[0] || 0
                                                                                    };

                                                                                    return updated;
                                                                                });
                                                                            }}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                                                        <Input type="text"
                                                                            value={v?.voyage}
                                                                            onChange={(e) => { handleVesselChange(i, 'voyage', e.target.value) }}
                                                                            className={inputClass}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                                                        <DatePicker
                                                                            selected={v?.cargoCutoff}
                                                                            onChange={(date) => handleVesselChange(i, 'cargoCutoff', date)}
                                                                            className={inputClass}
                                                                            showIcon
                                                                            dateFormat="dd MMM yyyy"
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                                                        <DatePicker
                                                                            selected={v?.etd}
                                                                            onChange={(date) => {
                                                                                handleVesselChange(i, 'etd', date);
                                                                                handleVesselChange(i, 'eta', null);
                                                                                handleVesselChange(i, 'transitTime', countDays(date, v?.eta));
                                                                            }}
                                                                            className={inputClass}
                                                                            showIcon
                                                                            dateFormat="dd MMM yyyy"
                                                                            minDate={new Date()}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                                                        <DatePicker
                                                                            selected={v?.eta}
                                                                            onChange={(date) => {
                                                                                handleVesselChange(i, 'eta', date);
                                                                                handleVesselChange(i, 'transitTime', countDays(v?.etd, date));
                                                                            }}
                                                                            className={inputClass}
                                                                            showIcon
                                                                            dateFormat="dd MMM yyyy"
                                                                            minDate={v?.etd ? moment(v?.etd).add(1, 'days') : new Date()}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                                                        <Input type="text"
                                                                            value={v?.transitTime}
                                                                            onChange={(e) => { handleVesselChange(i, 'transitTime', e.target.value) }}
                                                                            className={inputClass}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                                                        <button
                                                                            type="button"
                                                                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-all"
                                                                            title="Delete"
                                                                            onClick={() => { handleVesselChange(i, 'isDeleted', 1) }}
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        }

                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>

                                {/* Charges Section */}
                                <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <DollarSign className="w-4 h-4 text-emerald-500" />
                                            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Charges</h4>
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={addChargeRow}
                                            className="text-xs"
                                        >
                                            <Plus className="w-3 h-3 mr-1" /> Add Charge
                                        </Button>
                                    </div>
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        className="p-3"
                                    >
                                        <div>
                                            <div className=" overflow-x-auto overflow-y-visible pb-[20vh]">
                                                <table className="w-full text-xs ev_ar_table">
                                                    <thead>
                                                        <tr className="bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-800">
                                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Description</th>
                                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                                                                <input
                                                                    type='checkbox'
                                                                    className={inputClass + ' !w-4 h-4'}
                                                                    onChange={(e) => { e.target.checked ? settAllin(true) : settAllin(false) }}
                                                                />
                                                            </th>
                                                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Currency</th>
                                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">ROE</th>
                                                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Basis</th>
                                                            <th className="px-4 py-3 text-center text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase bg-emerald-50 dark:bg-emerald-900/20">
                                                                Sell <br /> {_getContainerName(0)}
                                                            </th>
                                                            <th className="px-4 py-3 text-center text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase bg-amber-50 dark:bg-amber-900/20">
                                                                Buy <br /> {_getContainerName(0)}
                                                            </th>
                                                            <th className="px-4 py-3 text-center text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase bg-emerald-50 dark:bg-emerald-900/20">
                                                                Sell <br /> {_getContainerName(1)}
                                                            </th>
                                                            <th className="px-4 py-3 text-center text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase bg-amber-50 dark:bg-amber-900/20">
                                                                Buy <br /> {_getContainerName(1)}
                                                            </th>
                                                            <th className="px-4 py-3 text-center text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase bg-emerald-50 dark:bg-emerald-900/20">
                                                                Sell <br /> {_getContainerName(2)}
                                                            </th>
                                                            <th className="px-4 py-3 text-center text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase bg-amber-50 dark:bg-amber-900/20">
                                                                Buy <br /> {_getContainerName(2)}
                                                            </th>
                                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Vendor</th>
                                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Payment Mode</th>
                                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                                        {
                                                            carriers.map((row, index) => row?.isdeleted == false && (
                                                                <tr key={index} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400 min-w-[200px]">
                                                                        <SearchBar
                                                                            searchAPI={'v1/Common/ChargesDescription_List?flag=0&searchKeyword='}
                                                                            selectReacord={(d) => {
                                                                                handleRateChange(index, 'chargedescription', d?.[1]);
                                                                                handleRateChange(index, 'desccode', d?.[0].toString());
                                                                            }}
                                                                            value={row?.chargedescription || ''}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400  text-center">
                                                                        <input type='checkbox' className={inputClass + ' !w-4 h-4'}
                                                                            checked={row?.all_in || false}
                                                                            onChange={(e) => handleRateChange(index, 'all_in', e.target.checked)}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400 max-w-[80px]">
                                                                        <select className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                                            value={row?.currency || ''}
                                                                            onChange={(e) => handleRateChange(index, 'currency', e.target.value)}
                                                                        >
                                                                            <option>Select</option>
                                                                            {
                                                                                currency_list?.map((currency, key) => (
                                                                                    <option key={key} value={currency.currencycode}>{currency.currencycode}</option>
                                                                                ))
                                                                            }
                                                                        </select>
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400 max-w-[80px]">
                                                                        <Input type="number"
                                                                            value={row?.roe || 1}
                                                                            onChange={(e) => handleRateChange(index, 'roe', e.target.value)}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400 max-w-[120px]">
                                                                        <select className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                                            value={row?.basis || ''}
                                                                            onChange={(e) => handleRateChange(index, 'basis', e.target.value)}
                                                                        >
                                                                            <option>Select</option>
                                                                            {
                                                                                charges_basis_list?.map((basis, key) => (
                                                                                    <option key={key} value={basis.chargebasis}>{basis.chargebasis}</option>
                                                                                ))
                                                                            }
                                                                        </select>
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400 min-w-[150px]">
                                                                        <Input type="number"
                                                                            value={row?.set1sellrate}
                                                                            onChange={(e) => handleRateChange(index, 'set1sellrate', e.target.value)}
                                                                            disabled={!_getContainerName(0)}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400 min-w-[150px]">
                                                                        <Input type="number"
                                                                            value={row?.set1buyrate}
                                                                            onChange={(e) => handleRateChange(index, 'set1buyrate', e.target.value)}
                                                                            disabled={!_getContainerName(0)}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400 min-w-[150px]">
                                                                        <Input type="number"
                                                                            value={row?.set2sellrate}
                                                                            onChange={(e) => handleRateChange(index, 'set2sellrate', e.target.value)}
                                                                            disabled={!_getContainerName(1)}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400 min-w-[150px]">
                                                                        <Input type="number"
                                                                            value={row?.set2buyrate}
                                                                            onChange={(e) => handleRateChange(index, 'set2buyrate', e.target.value)}
                                                                            disabled={!_getContainerName(1)}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400 min-w-[150px]">
                                                                        <Input type="number"
                                                                            value={row?.set3sellrate}
                                                                            onChange={(e) => handleRateChange(index, 'set3sellrate', e.target.value)}
                                                                            disabled={!_getContainerName(2)}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400 min-w-[150px]">
                                                                        <Input type="number"
                                                                            value={row?.set3buyrate}
                                                                            onChange={(e) => handleRateChange(index, 'set3buyrate', e.target.value)}
                                                                            disabled={!_getContainerName(2)}
                                                                        />
                                                                    </td>

                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400 min-w-[200px]">
                                                                        <SearchBar
                                                                            searchAPI={'v1/AutoSuggestion/GetVendorSuggestion?Prefix='}
                                                                            selectReacord={(d) => {
                                                                                handleRateChange(index, 'vendor_name', d?.[1]);
                                                                                handleRateChange(index, 'vendorid', d?.[0]);
                                                                            }}
                                                                            value={row?.vendor_name || ''}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                                                        <select className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                                            value={row?.paymentmode || ''}
                                                                            onChange={(e) => handleRateChange(index, 'paymentmode', e.target.value)}
                                                                        >
                                                                            <option value="">Select</option>
                                                                            {
                                                                                ['P', 'C', 'N'].map((mode) => (
                                                                                    <option key={mode} value={mode}>{mode === 'P' ? 'Prepaid' : mode === 'C' ? 'Collect' : 'None'}</option>
                                                                                ))
                                                                            }
                                                                        </select>
                                                                    </td>
                                                                    <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                                                        <button
                                                                            type="button"
                                                                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-all"
                                                                            title="Delete"
                                                                            onClick={() => { handleRateChange(index, 'isdeleted', true) }}
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        }

                                                    </tbody>
                                                    <tfoot>
                                                        <tr className='bg-[#49c3d487] dark:bg-[#012641]'>
                                                            <th></th>
                                                            <th></th>
                                                            <th></th>
                                                            <th></th>
                                                            <th className='p-4'>
                                                                <span className="text-sm text-slate-600 dark:text-white">Total</span>
                                                            </th>
                                                            <th className='p-2'>
                                                                <div className="flex items-center gap-2 px-4 py-2 bg-[#edede9] dark:bg-[#012a4a] rounded-lg justify-center">
                                                                    <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                                                                        ${parseFloat(formData?.set1TotalSell ?? 0).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                            </th>
                                                            <th className='p-2'>
                                                                <div className="flex items-center gap-2 px-4 py-2 bg-[#edede9] dark:bg-[#012a4a] rounded-lg justify-center">
                                                                    <span className="text-lg font-bold text-amber-600 dark:text-amber-400">
                                                                        ${parseFloat(formData?.set1TotalBuy ?? 0).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                            </th>
                                                            <th className='p-2'>
                                                                <div className="flex items-center gap-2 px-4 py-2 bg-[#edede9] dark:bg-[#012a4a] rounded-lg justify-center">
                                                                    <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
                                                                        ${parseFloat(formData?.set2TotalSell ?? 0).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                            </th>
                                                            <th className='p-2'>
                                                                <div className="flex items-center gap-2 px-4 py-2 bg-[#edede9] dark:bg-[#012a4a] rounded-lg justify-center">
                                                                    <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                                                                        ${parseFloat(formData?.set2TotalBuy ?? 0).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                            </th>
                                                            <th className='p-2'>
                                                                <div className="flex items-center gap-2 px-4 py-2 bg-[#edede9] dark:bg-[#012a4a] rounded-lg justify-center">
                                                                    <span className="text-lg font-bold text-amber-600 dark:text-amber-400">
                                                                        ${parseFloat(formData?.set3TotalSell ?? 0).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                            </th>
                                                            <th className='p-2'>
                                                                <div className="flex items-center gap-2 px-4 py-2 bg-[#edede9] dark:bg-[#012a4a] rounded-lg justify-center">
                                                                    <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
                                                                        ${parseFloat(formData?.set3TotalBuy ?? 0).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                            </th>
                                                            <th></th>
                                                            <th></th>
                                                            <th></th>
                                                        </tr>
                                                        <tr className='bg-[#89c2d9] dark:bg-[#013a63]'>
                                                            <th></th>
                                                            <th></th>
                                                            <th></th>
                                                            <th></th>
                                                            <th className='p-4'>
                                                                <span className="text-sm text-slate-600 dark:text-white">Total Profit ($)</span>
                                                            </th>
                                                            <th colSpan={2} className='p-2'>
                                                                <div className="flex items-center gap-2 px-4 py-2 bg-[#edede9] dark:bg-[#012a4a] rounded-lg justify-center">
                                                                    <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                                                                        ${parseFloat(formData?.set1Profit ?? 0).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                            </th>
                                                            <th colSpan={2} className='p-2'>
                                                                <div className="flex items-center gap-2 px-4 py-2 bg-[#edede9] dark:bg-[#012a4a] rounded-lg justify-center">
                                                                    <span className="text-lg font-bold text-amber-600 dark:text-amber-400">
                                                                        ${parseFloat(formData?.set2Profit ?? 0).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                            </th>
                                                            <th colSpan={2} className='p-2'>
                                                                <div className="flex items-center gap-2 px-4 py-2 bg-[#edede9] dark:bg-[#012a4a] rounded-lg justify-center">
                                                                    <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
                                                                        ${parseFloat(formData?.set3Profit ?? 0).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                            </th>
                                                            <th></th>
                                                            <th></th>
                                                            <th></th>
                                                        </tr>
                                                        <tr className='bg-[#c1dff0] dark:bg-[#88ccf1]'>
                                                            <th></th>
                                                            <th></th>
                                                            <th></th>
                                                            <th></th>
                                                            <th className='p-4'>
                                                                <span className="text-sm text-slate-600 dark:text-white">Total Margin (%)</span>
                                                            </th>
                                                            <th colSpan={2} className='p-2'>
                                                                <div className="flex items-center gap-2 px-4 py-2 bg-[#edede9] dark:bg-[#012a4a] rounded-lg justify-center">
                                                                    <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                                                                        {parseFloat(formData?.set1ProfitMargin ?? 0).toFixed(2)}%
                                                                    </span>
                                                                </div>
                                                            </th>
                                                            <th colSpan={2} className='p-2'>
                                                                <div className="flex items-center gap-2 px-4 py-2 bg-[#edede9] dark:bg-[#012a4a] rounded-lg justify-center">
                                                                    <span className="text-lg font-bold text-amber-600 dark:text-amber-400">
                                                                        {parseFloat(formData?.set2ProfitMargin ?? 0).toFixed(2)}%
                                                                    </span>
                                                                </div>
                                                            </th>
                                                            <th colSpan={2} className='p-2'>
                                                                <div className="flex items-center gap-2 px-4 py-2 bg-[#edede9] dark:bg-[#012a4a] rounded-lg justify-center">
                                                                    <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
                                                                        {parseFloat(formData?.set3ProfitMargin ?? 0).toFixed(2)}%
                                                                    </span>
                                                                </div>
                                                            </th>
                                                            <th></th>
                                                            <th></th>
                                                            <th></th>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>


                    {showModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-3">
                            <div className="bg-white dark:bg-slate-800 p-4 sm:p-6 rounded-lg w-full sm:w-[90%] md:w-[80%] lg:w-[70%] max-w-4xl shadow-xl">
                                {/* Heading */}
                                <h2 className="text-lg sm:text-xl font-bold mb-4 text-center text-black dark:text-white">
                                    Vessel List
                                </h2>
                                {/* Scroll Wrapper */}
                                <div className="max-h-[300px] overflow-auto border border-gray-300 dark:border-slate-600">
                                    <table className="w-full table-fixed border-collapse text-center">
                                        {/* Header */}
                                        <thead className="bg-gray-200 dark:bg-slate-700 text-black dark:text-white sticky top-0 z-10">
                                            <tr>
                                                <th className="border p-2 w-[120px]">Action</th>
                                                <th className="border p-2 w-[30%]">Vessel</th>
                                                <th className="border p-2 w-[30%]">Voyage</th>
                                                <th className="border p-2 w-[30%]">ETD</th>
                                                <th className="border p-2 w-[30%]">ETA</th>
                                                <th className="border p-2 w-[30%]">Port C/O</th>
                                                <th className="border p-2 w-[30%]">TT</th>
                                            </tr>
                                        </thead>
                                        {/* Body */}
                                        <tbody>
                                            {
                                                vesselList.map((item, index) => (
                                                    <tr
                                                        key={index}
                                                        className="bg-white dark:bg-slate-800 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-slate-700"
                                                    >
                                                        {/* <td className="border p-2 w-[120px]">
                                                            <button
                                                                onClick={() => handleSelectVessel(item)}
                                                                className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                                                            >
                                                                Select
                                                            </button>
                                                    </td> */}
                                                        <td className="border p-2 w-[120px]">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedVessels.includes(item)}
                                                                onChange={(e) => handleCheckboxChange(item, e.target.checked)}
                                                            />
                                                        </td>
                                                        <td className="border p-2 break-words">{item.vesselName}</td>
                                                        <td className="border p-2 break-words">{item.voyageNumber}</td>
                                                        <td className="border p-2 break-words">{item.originDepartureDate}</td>
                                                        <td className="border p-2 break-words">{item.destinationArrivalDate}</td>
                                                        <td className="border p-2 break-words">{item.terminalCutoff}</td>
                                                        <td className="border p-2 break-words">{item.totalDuration}</td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Footer */}
                                <div className="flex justify-center mt-4">
                                    <button
                                        onClick={handleSaveVessels}
                                        className="px-4 py-1 bg-green-600 hover:bg-green-700 text-white rounded"
                                    >
                                        Save
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSelectedVessels([]);   // ✅ clear selected data
                                            setShowModal(false);      // close modal
                                        }}
                                        className="px-4 py-1 bg-red-500 hover:bg-red-600 text-white rounded"
                                    >
                                        Close
                                    </button>
                                </div>
                                {error && (
                                    <div className="text-red-500 text-sm text-center mb-2">
                                        {error}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                </motion.div>
            </div>

            <AlertModal
                open={alert.open}
                onOpenChange={alert.handleOpenChange}
                title={alert.title}
                description={alert.description}
                icon={alert.icon}
                iconColor={alert.iconColor}
                confirmText={alert.confirmText}
                cancelText={alert.cancelText}
                onConfirm={alert.handleConfirm}
                onCancel={alert.handleCancel}
                confirmButtonColor={alert.confirmButtonColor}
            />
        </div>
    );
};

export default MultiCarrierSection;